import React from 'react';
import type { ActivityListRow as ActivityListRowModel } from 'coop-adventure-engine';
import {
  isRichTextDescription,
  plainTextToRichText,
  renderRichText
} from '../../utils/richTextRenderer';
import Button from './Button';

export interface ActivityListProps {
  rows: ActivityListRowModel[];
  onRowNavigate?: (row: ActivityListRowModel) => void;
  /**
   * Scene id from embed `redo_scene=`; required for REDO on completed rows (separate from {@link onRowNavigate}).
   */
  redoTargetScene?: string;
  /** When set with {@link redoTargetScene}, the REDO button calls this with the activity id (server sets `activity_to_redo`). */
  onActivityRedo?: (activityId: string) => void;
  /**
   * Present when `<Activity-List>` embed uses reference selection; disables start-scene navigation and
   * drives {@link onActivityReferencePick} instead.
   */
  referenceSelect?: {
    targetScene: string;
    variableName: string;
    selectionAllowLocked: boolean;
  };
  /** Fired with activity id when reference mode is active. */
  onActivityReferencePick?: (activityId: string) => void;
  /** When true, enabled rows do not navigate (e.g. global action pending). */
  disableInteractions?: boolean;
  customColor?: string | undefined;
  className?: string;
  /** When false, prerequisite lines are not shown on cards (embed `hide_requirements`). */
  showRequirements?: boolean;
}

/**
 * Renders engine-computed activity rows for an `<Activity-List>` embed.
 */
const ActivityList: React.FC<ActivityListProps> = ({
  rows,
  onRowNavigate,
  redoTargetScene,
  onActivityRedo,
  referenceSelect,
  onActivityReferencePick,
  disableInteractions = false,
  customColor,
  className = '',
  showRequirements = true
}) => {
  if (!rows.length) {
    return null;
  }

  const activityNameById = new Map(rows.map((r) => [r.activityId, r.name]));

  const resolveRequirementLabel = (
    req: ActivityListRowModel['requirements'][number]
  ): string => {
    const [kindRaw, targetIdRaw] = String(req.key || '').split(':');
    const kind = (kindRaw || '').trim().toLowerCase();
    const targetId = (targetIdRaw || '').trim();
    if (kind === 'completed' || kind === 'active') {
      if (targetId) {
        const fromVisibleRow = activityNameById.get(targetId);
        if (fromVisibleRow) {
          return fromVisibleRow;
        }
        // Prerequisite not on this list (e.g. course ids on the mission board only show missions).
        // Use the engine-provided label ("Complete: <display name>").
        const fromLabel = String(req.label || '')
          .replace(/^(Complete|Active):\s*/i, '')
          .trim();
        if (fromLabel) {
          return fromLabel;
        }
        return targetId;
      }
    }
    return req.label;
  };

  const renderDescriptionHtml = (row: ActivityListRowModel): string | null => {
    const raw = row.description;
    if (raw === undefined || raw === null || raw === '') {
      return null;
    }
    const rich = isRichTextDescription(raw) ? raw : plainTextToRichText(raw);
    const html = renderRichText(rich).trim();
    return html.length > 0 ? html : null;
  };

  const getDisplayRequirements = (
    row: ActivityListRowModel
  ): ActivityListRowModel['requirements'] => {
    return row.requirements.filter((req) => {
      const [kindRaw, targetIdRaw] = String(req.key || '').split(':');
      const kind = (kindRaw || '').trim().toLowerCase();
      const targetId = (targetIdRaw || '').trim();

      // Show only "Requires Completed" prerequisites in the card.
      // Active requirements still affect enabled/disabled server-side.
      if (kind === 'completed') {
        return targetId.length > 0 && targetId !== row.activityId;
      }
      return false;
    });
  };

  return (
    <ul
      className={`activity-list my-4 space-y-3 list-none p-0 ${className}`.trim()}
      data-testid="activity-list"
      aria-label="Activities"
    >
      {rows.map((row) => {
        const refMode = Boolean(referenceSelect && onActivityReferencePick);
        const selectableForReference =
          refMode &&
          (row.enabled || referenceSelect!.selectionAllowLocked) &&
          !disableInteractions &&
          Boolean(onActivityReferencePick);
        const canPlayNavigate =
          !refMode && row.enabled && !disableInteractions && Boolean(onRowNavigate);
        const canNavigate = selectableForReference || canPlayNavigate;
        const displayRequirements = getDisplayRequirements(row);
        const isCompleted = row.requirements.some((req) => req.key === 'done' && req.met);
        const isInProgress = Boolean(row.isInProgress) && !isCompleted;
        const canRedo =
          !refMode &&
          isCompleted &&
          !disableInteractions &&
          Boolean(redoTargetScene) &&
          typeof onActivityRedo === 'function';

        const isActiveCard =
          ((!refMode && row.enabled) || selectableForReference) &&
          !disableInteractions;
        const activeStyle: React.CSSProperties | undefined = isActiveCard && customColor
          ? {
              border: `1px solid ${customColor}40`,
              background: `linear-gradient(135deg, ${customColor}88, ${customColor}AA)`
            }
          : undefined;

        const descriptionHtml = renderDescriptionHtml(row);

        const inner = (
          <>
            <div className="activity-card-header">
              <span className="activity-card-title">
                <span className="activity-card-title-leading">{row.name}</span>
                {isCompleted ? (
                  <span className="activity-card-completed-actions">
                    <span className="activity-card-title-tick" aria-hidden="true">
                      ✓
                    </span>
                    {canRedo ? (
                      <Button
                        type="button"
                        variant="glass-primary"
                        size="sm"
                        customColor={customColor}
                        className="activity-card-redo-button"
                        onClick={() => onActivityRedo?.(row.activityId)}
                        data-testid={`activity-redo-${row.activityId}`}
                        aria-label={`Redo activity: ${row.name}`}
                      >
                        REDO
                      </Button>
                    ) : null}
                  </span>
                ) : isInProgress ? (
                  <span
                    className="activity-card-status-in-progress"
                    role="status"
                  >
                    In-progress
                  </span>
                ) : null}
              </span>
            </div>
            {descriptionHtml ? (
              <div
                className="activity-card-description"
                style={{ fontStyle: 'italic' }}
                data-testid={`activity-desc-${row.activityId}`}
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            ) : null}
            {showRequirements &&
              !isCompleted &&
              displayRequirements.length > 0 && (
              <ul
                className="activity-card-requirements"
                aria-label={`Requirements for ${row.name}`}
              >
                <li className="activity-card-requirements-title">Requirements:</li>
                {displayRequirements.map((req) => (
                  <li
                    key={req.key}
                    className={`activity-card-requirement ${req.met ? 'is-met' : 'is-unmet'}`}
                    data-testid={`activity-req-${row.activityId}-${req.key}`}
                  >
                    <span>{resolveRequirementLabel(req)}</span>
                    {req.met ? <span aria-hidden="true">✓</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </>
        );

        const cardClass = `activity-card ${isActiveCard ? 'is-active' : 'is-inactive'}`;

        if (canNavigate) {
          return (
            <li key={row.activityId}>
              <button
                type="button"
                className={cardClass}
                style={activeStyle}
                onClick={() =>
                  refMode
                    ? onActivityReferencePick?.(row.activityId)
                    : onRowNavigate?.(row)
                }
                data-testid={`activity-row-${row.activityId}`}
                aria-label={
                  refMode
                    ? `Select activity: ${row.name}`
                    : isInProgress
                      ? `Continue activity: ${row.name}`
                      : `Start activity: ${row.name}`
                }
              >
                {inner}
              </button>
            </li>
          );
        }

        return (
          <li key={row.activityId}>
            <div
              className={cardClass}
              style={activeStyle}
              data-testid={`activity-row-${row.activityId}`}
              aria-disabled="true"
              role="group"
              aria-label={`${row.name}, unavailable`}
            >
              {inner}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default ActivityList;
